/**
 * HandwritingManager - Facade-facing API for handwriting-to-text.
 *
 * Thin wrapper over HandwritingDragger so UI code never touches the
 * dragger directly (same pattern as TextManager/TextDragger).
 */

import type { HandwritingDragger } from "../draggers/HandwritingDragger";
import type {
  HandwritingRecognizer,
  HandwritingStatus,
  HandwritingStatusListener,
} from "../handwriting/types";

export interface HandwritingManagerDeps {
  handwritingDragger: HandwritingDragger;
}

export class HandwritingManager {
  private dragger: HandwritingDragger;

  constructor(deps: HandwritingManagerDeps) {
    this.dragger = deps.handwritingDragger;
  }

  /** Inject the recognition engine (mockable, swappable). */
  setRecognizer(recognizer: HandwritingRecognizer): void {
    this.dragger.setRecognizer(recognizer);
  }

  /** Current capture/conversion status. */
  get status(): HandwritingStatus {
    return this.dragger.getStatus();
  }

  /** Technical detail of the last failure (null when none). */
  get lastError(): string | null {
    return this.dragger.getLastError();
  }

  /** Whether strokes are pending on the canvas. */
  get hasInk(): boolean {
    return this.dragger.hasInk();
  }

  /** Subscribe to status changes. Returns an unsubscribe function. */
  onStatusChange(listener: HandwritingStatusListener): () => void {
    return this.dragger.onStatusChange(listener);
  }

  /** Convert the pending ink immediately (toolbar ✓ button). */
  convertNow(): void {
    this.dragger.convertNow();
  }

  /** Drop the pending ink without converting. */
  discardInk(): void {
    this.dragger.discardInk();
  }
}
